import { AMPLITUDE_EVENT } from '@sonar-software/sonar-analytics';
import { getInitiativeRoute } from '@lib/routes';
import { getInitiativeTableColumns } from './InitiativesTableColumns';
import { InitiativesTableActionBar } from './InitiativesTableActionBar';
import isEqual from 'lodash/isEqual';
import { useNavigate } from 'react-router-dom';
import { COPY_SPECIFIC, INITIATIVES_TABLE_KEYS } from '@sonar-software/sonar-ts-constants';
import { KeyPagination, keyPaginationFallbackQueryParams } from '@ui/KeyPagination';
import { PaginationDirection } from '@sonar-software/sonar-ts-types';
import React, { useCallback, useMemo, useRef } from 'react';
import { SelectionTable } from '@ui/tables';
import { useAmplitude, useCurrentUser, useInitiatives, useQueryParams, useSelectionTableWithInversion } from '@hooks';
const columns = getInitiativeTableColumns();
const statusColumn = columns.find((x) => x.key === INITIATIVES_TABLE_KEYS.STATUS);
const assignedToColumn = columns.find((x) => x.key === INITIATIVES_TABLE_KEYS.ASSIGNED_TO);
const getRowKey = (initiative) => initiative.id;
export const InitiativesTable = ({ id }) => {
    const navigate = useNavigate();
    const logEvent = useAmplitude();
    const { isReadOnly } = useCurrentUser();
    const { data, request, isRequested } = useInitiatives(true);
    const { results, totalCount } = data;
    const scrollableListRef = useRef(null);
    const selectionData = useSelectionTableWithInversion({
        getRowKey,
        results,
        totalCount
    });
    const { queryParams, updateParams } = useQueryParams({
        defaultParams: useInitiatives.defaultRequest,
        fallbackParams: keyPaginationFallbackQueryParams,
        onUpdate: (newParams, prev, combined) => {
            const didSearchChange = combined.title !== (prev === null || prev === void 0 ? void 0 : prev.title);
            const didStatusChange = !isEqual(combined.statuses, prev === null || prev === void 0 ? void 0 : prev.statuses);
            const didAssignedTosChange = !isEqual(combined.assignedTos, prev === null || prev === void 0 ? void 0 : prev.assignedTos);
            const didTableFiltersChange = didSearchChange || didStatusChange || didAssignedTosChange;
            request(combined);
            if (didTableFiltersChange) {
                selectionData === null || selectionData === void 0 ? void 0 : selectionData.resetSelection();
            }
        }
    });
    const calculatedColumns = useMemo(() => {
        var _a;
        return [
            ...columns.slice(0, 1),
            Object.assign(Object.assign({}, statusColumn), { filteredValue: queryParams.statuses }),
            Object.assign(Object.assign({}, assignedToColumn), { filteredValue: (_a = queryParams.assignedTos) === null || _a === void 0 ? void 0 : _a.map((userId) => String(userId)) }),
            ...columns.slice(3)
        ];
    }, [queryParams.assignedTos, queryParams.statuses]);
    const handleRowClick = useCallback((record) => {
        logEvent({ id: AMPLITUDE_EVENT.INITIATIVE_DETAILS_VIEWED });
        navigate(getInitiativeRoute(record.id));
    }, [logEvent, navigate]);
    const handleOnTableChange = useCallback((pagination, filters, sorter) => {
        var _a, _b, _c;
        const sortDirection = `${sorter.order || 'descend'}ing`;
        const didSortDirectionChange = sortDirection !== queryParams.sortDirection;
        updateParams(Object.assign(Object.assign(Object.assign({}, queryParams), { assignedTos: (_b = (_a = filters[INITIATIVES_TABLE_KEYS.ASSIGNED_TO]) === null || _a === void 0 ? void 0 : _a.map((userId) => Number(userId))) !== null && _b !== void 0 ? _b : [], sortBy: sorter.columnKey, sortDirection, statuses: (_c = filters[INITIATIVES_TABLE_KEYS.STATUS]) !== null && _c !== void 0 ? _c : [] }), didSortDirectionChange ?
            {
                key: '',
                paginationDirection: PaginationDirection.Forward
            } :
            {}));
    }, [queryParams, updateParams]);
    return (React.createElement(React.Fragment, null,
        React.createElement(SelectionTable, { actionBar: React.createElement(InitiativesTableActionBar, { onUpdate: updateParams, queryParams: queryParams, selectionData: selectionData }), bordered: true, columns: calculatedColumns, dataSource: results, forceShowSelection: !isReadOnly, id: id, loading: isRequested, onChange: handleOnTableChange, onRowClick: handleRowClick, ref: scrollableListRef, rowKey: getRowKey, selectionData: selectionData, selectionType: COPY_SPECIFIC.INITIATIVES }),
        React.createElement(KeyPagination, { autoHide: true, defaultPageSize: Number(queryParams.count), listRef: scrollableListRef, onChange: updateParams, resource: data })));
};
